/*
 * Copyright (C) 2020 Airfordable, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import { styled, Theme, Typography } from '@material-ui/core';

import {
  Col,
  Container,
  Illustration,
  IllustrationProps,
  TitleHighlight,
} from 'components/UI';
import { Breakpoints } from 'utils/enums';

const FooterNavigation = styled('div')(({ theme }) => {
  return {
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(6, 0, 0),
    width: '100%',

    [theme.breakpoints.up(Breakpoints.Md)]: {
      padding: theme.spacing(16, 0, 16),
    },
  };
});

const FooterContainer = styled(Container)(({ theme }) => ({
  flexDirection: 'column',
  [theme.breakpoints.up(Breakpoints.Md)]: {
    flexDirection: 'row',
  },
}));

const FooterText = styled(Typography)(({ theme }) => ({
  '&:first-child': {
    marginTop: theme.spacing(4),
  },
  color: theme.palette.secondary.dark,
  margin: 0,
}));

const FooterCopyright = styled('div')(({ theme }) => ({
  maxWidth: '100%',

  [theme.breakpoints.up(Breakpoints.Md)]: {
    maxWidth: theme.typography.pxToRem(224),
  },
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  margin: 0,

  [theme.breakpoints.up(Breakpoints.Md)]: {
    margin: theme.spacing(0, 0, 4, 0),
  },
}));

const FooterCol = styled(Col)(({ theme }) => ({
  display: 'flex',
  width: '100%',

  [theme.breakpoints.up(Breakpoints.Md)]: {
    width: 'unset',
  },
}));

const FooterColSocial = styled(FooterCol)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  [theme.breakpoints.up(Breakpoints.Md)]: {
    alignItems: 'flex-end',
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(3.75),
    marginLeft: theme.spacing(11.25),
  },
}));

const FooterSocial = styled('div')(({ theme }) => ({
  '& .footer-desktop__text-social': {
    '&:after': {
      content: '"·"',
      margin: theme.spacing(0, 1, 0),
    },
    '&:last-child': {
      '&:after': {
        display: 'none',
      },
    },
    position: 'relative',
  },
  display: 'flex',
}));

const PreFooterWrapper = styled('div')(({ theme }) => ({
  '& .pre-footer-image:nth-child(1)': {
    '& .asset': {
      right: '-27%',
      width: '55%',
    },
    order: 0,
    top: '-2.5em',
  },
  '& .pre-footer-image:nth-child(2)': {
    '& .asset': {
      left: '11%',
      top: '19%',
      width: '73%',
    },
    order: 1,
    top: '-6em',
  },
  '& .pre-footer-image:nth-child(3)': {
    '& .asset': {
      left: '-20%',
      top: '11%',
      width: '130%',
    },
    display: 'none',
    order: 4,
    top: '-2.5em',
    [theme.breakpoints.up(Breakpoints.Md)]: {
      display: 'block',
    },
  },
  '& .pre-footer-image:nth-child(4)': {
    '& .asset': {
      left: '15%',
      top: '5%',
      width: '36%',
    },
    order: 5,
    top: '-2.5em',
    [theme.breakpoints.up(Breakpoints.Md)]: {
      top: '-6em',
    },
  },
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexWrap: 'wrap',
  marginLeft: theme.spacing(-10),
  marginRight: theme.spacing(-10),
  marginTop: theme.spacing(35),

  [theme.breakpoints.up(Breakpoints.Md)]: {
    marginLeft: theme.spacing(-30),
    marginRight: theme.spacing(-30),
    marginTop: theme.spacing(70),
  },
}));

const PreFooterContent = styled('div')(({ theme }) => ({
  '& > div': {
    margin: 'auto',
  },
  display: 'flex',
  marginBottom: theme.spacing(10),
  order: 5,
  textAlign: 'center',
  width: '100%',

  [theme.breakpoints.up(Breakpoints.Md)]: {
    order: 3,
    width: '30%',
  },

  [theme.breakpoints.up(1080)]: {
    width: '40%',
  },
}));

const PreFooterImageWrap = styled('div')(({ theme }) => ({
  borderRadius: theme.borderRadius[2],
  display: 'flex',
  position: 'relative',
  width: 'calc(100% / 3)',

  [theme.breakpoints.up(Breakpoints.Md)]: {
    width: 'calc(70% / 4)',
  },

  [theme.breakpoints.up(1080)]: {
    width: 'calc(60% / 4)',
  },
}));

const PreFooterImage = styled(Illustration)<Theme, IllustrationProps>(
  ({ theme }) => ({
    margin: 'auto',
    maxWidth: theme.spacing(57),
    width: '90%',
  })
);

const PreFooterTitle = styled(TitleHighlight)(({ theme }) => ({
  '& span': {
    display: 'block',
  },
  fontWeight: 500,
  marginBottom: theme.spacing(8),
  ...theme.typography.h4,

  [theme.breakpoints.up(680)]: {
    ...theme.typography.h2,
    fontWeight: 500,
  },
}));

export {
  FooterCol,
  FooterColSocial,
  FooterContainer,
  FooterCopyright,
  FooterNavigation,
  FooterSocial,
  FooterText,
  FooterTitle,
  PreFooterContent,
  PreFooterImage,
  PreFooterImageWrap,
  PreFooterTitle,
  PreFooterWrapper,
};
